import { isEqual } from 'lodash';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useRecoilState } from 'recoil';

import { getBidDefaults } from '../modules/bid-buy-helpers';
import { MonetaryAmountStrict } from '../modules/currency';
import { DetailCarDTO, PackageDetails, SourceType, SupportedPriceType, ValidityType } from '../modules/generated/api';
import { kvpsLocalStorageKey } from '../modules/global-vars';
import { setToArray } from '../modules/util';
import { chosenValuationCountryState } from '../stores/state';
import BidFormData from '../types/BidFormData';

import { BidBuyFormType, noKVPSSources } from './useBidBuyForm';
import { useCalculationPackage } from './useCalculationPackage';
import { useCurrency } from './useCurrency';
import useRole from './useRole';

export const useBidBuyFormPackage = (packageDetails: PackageDetails) => {
  const { hasRole } = useRole();
  const { convert } = useCurrency();
  const [chosenValuationCountry] = useRecoilState(chosenValuationCountryState);
  const { calculations, fetchCalculation } = useCalculationPackage(
    packageDetails.source as SourceType,
    packageDetails.packageId as string,
  );
  const { bid, deliveryAddressSelection, kvps = [] } = packageDetails;
  // Some sources don't use KVPS (dealer identifier) — delivery address logic differs for them
  const isNoKVPSCar = noKVPSSources.includes(packageDetails.source!);

  // The first bid in the package is used as the reference for pre-filling KVPS and delivery address
  const kvpsAddressReferenceBid = bid?.bids ? bid.bids[0] : undefined;

  // Default KVPS: prefer the one already used in the existing bid, fall back to the last value stored in localStorage
  const kvpsLocalStorage = localStorage.getItem(kvpsLocalStorageKey) || '';
  const defaultKvps =
    kvpsAddressReferenceBid?.kvps ||
    (kvps.every((kvpsDTO) => kvpsDTO.kvps !== kvpsLocalStorage) ? '' : kvpsLocalStorage);

  // Default delivery address priority: address used in existing bid → address mapped to default KVPS → first global address (no-KVPS sources)
  const defaultDeliveryAddress =
    kvpsAddressReferenceBid?.deliveryAddress?.id ||
    deliveryAddressSelection?.selectedAddressByKVPS?.[defaultKvps]?.[0] ||
    (isNoKVPSCar && setToArray(deliveryAddressSelection?.deliveryAddresses).find((address) => address.global)?.id) ||
    '';

  const formDefaults: BidBuyFormType = useMemo(() => {
    // Minimal DetailCarDTO shaped object required by getBidDefaults to compute bid amount defaults
    const carForDefaults: DetailCarDTO = {
      source: packageDetails.source,
      packageId: packageDetails.packageId,
      bid: packageDetails.bid,
      previousAuctions: packageDetails.previousAuctions,
      bidStateInfo: { allHistoricBids: packageDetails.bid?.bids },
      validity: ValidityType.Current,
    };
    return {
      kvps: defaultKvps,
      deliveryAddress: defaultDeliveryAddress,
      bid: {
        ...getBidDefaults({
          car: carForDefaults,
          hasRole,
          convert,
        }),
      } as BidFormData,
      overrideFirstCallCascade: 'no',
      overridingFirstCallCascadeDealer: undefined,
      autoSetBids: false,
      selectedTransportOption: packageDetails.bid?.selectedTransportOption?.id || '',
    };
  }, [packageDetails, convert, hasRole, defaultKvps, defaultDeliveryAddress]);

  const form = useForm<BidBuyFormType>({ mode: 'all', defaultValues: formDefaults });
  const { setValue, trigger, reset, watch } = form;
  const watchKvps = watch('kvps');

  // Read calculations via a ref so resetAndRefetch can access the latest cached values without
  // including calculations in its deps — adding it as a dep would cause an infinite loop:
  // each fetch resolves → setCalculations → calculations changes → resetAndRefetch changes → effect fires → fetches again.
  const calculationsRef = useRef(calculations);
  calculationsRef.current = calculations;

  // Resets the form to current defaults and re-triggers gross price calculations for all 3 bid slots.
  // If a cached calculation already matches the current input and valuation country, it is applied immediately
  // before the async fetch resolves to avoid a flicker.
  // calculations is intentionally read via ref and not listed as a dep — including it would cause an
  // infinite loop: fetch resolves → setCalculations → calculations changes → resetAndRefetch changes → effect fires again.
  const resetAndRefetch = useCallback(() => {
    reset(formDefaults);
    [1, 2, 3].forEach((bidNr) => {
      const bidNumber = bidNr as 1 | 2 | 3;
      const existingResponse = calculationsRef.current[bidNumber];
      const inputValue = formDefaults.bid[`bid${bidNumber}` as keyof BidFormData] as MonetaryAmountStrict | undefined;
      if (isEqual(existingResponse?.base, inputValue) && chosenValuationCountry === existingResponse?.country) {
        setValue(`bid.bid-gross-${bidNumber}`, existingResponse?.purchaseGross as MonetaryAmountStrict);
      }
      fetchCalculation({
        fixedVariable: SupportedPriceType.OfferPrice,
        value: inputValue,
        valuationCountry: chosenValuationCountry,
        bidNumber,
      }).then((value) => {
        if (value?.purchaseGross) setValue(`bid.bid-gross-${bidNumber}`, value?.purchaseGross as MonetaryAmountStrict);
      });
    });
  }, [formDefaults, chosenValuationCountry, reset, setValue, fetchCalculation]);

  // Keep the delivery address field in sync whenever the selected KVPS or available addresses change.
  // Only reuse the address from an existing bid if it is still valid for the current KVPS selection.
  useEffect(() => {
    const allowedDeliveryAddressIds = isNoKVPSCar
      ? setToArray(deliveryAddressSelection?.deliveryAddresses).map((address) => address.id)
      : deliveryAddressSelection?.selectedAddressByKVPS?.[watchKvps];
    const deliveryAddressUsedInBid = kvpsAddressReferenceBid?.deliveryAddress?.id;
    const deliveryAddressUsedInBidSafe =
      deliveryAddressUsedInBid &&
      allowedDeliveryAddressIds?.includes(deliveryAddressUsedInBid) &&
      (!watchKvps || watchKvps === kvpsAddressReferenceBid?.kvps) &&
      deliveryAddressUsedInBid;

    setValue('deliveryAddress', deliveryAddressUsedInBidSafe || allowedDeliveryAddressIds?.[0] || '', {
      shouldValidate: true,
    });
  }, [
    setValue,
    deliveryAddressSelection?.deliveryAddresses,
    deliveryAddressSelection?.selectedAddressByKVPS,
    kvpsAddressReferenceBid,
    watchKvps,
    isNoKVPSCar,
  ]);

  // Re-validate KVPS and delivery address whenever package details change (e.g. after a refetch)
  useEffect(() => {
    trigger(['kvps', 'deliveryAddress']);
  }, [packageDetails, trigger]);

  // Persist the selected KVPS to localStorage so it can be pre-filled next time, but only when it
  // was not already set by an existing bid (to avoid overwriting a bid-specific value)
  useEffect(() => {
    if (watchKvps && !kvpsAddressReferenceBid?.kvps) window.localStorage.setItem(kvpsLocalStorageKey, watchKvps);
  }, [watchKvps, kvpsAddressReferenceBid?.kvps]);

  // Run resetAndRefetch whenever formDefaults change (i.e. package details or valuation country updated)
  useEffect(() => {
    resetAndRefetch();
  }, [resetAndRefetch]);

  return {
    form,
    resetAndRefetch,
    fetchCalculation,
  };
};
